import { inject, Injectable } from '@angular/core';
import { BackendAbstractService } from '../../abstract/backend-abstract.service';
import { Observable, of } from 'rxjs';
import {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  Credentials,
  LoginResponseInterface,
  RefreshTokenRequest,
  SmsChallengeRequest,
} from '../../../models';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { LoginResponse } from '../../../models/auth.model';
import { ignoreInterceptorHeaders } from '@rx-applications/shared/utils';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ImpersonateCookie } from './consts/impersonate-cookie.enum';
import { ImpersonationResponse } from '../../../models/impersonation-response.model';
import { SetupMfaRequest } from '../../../models/setup-mfa-request.model';
import { SsoLogin } from '../../../models/sso-login.model';

@Injectable({
  providedIn: 'root',
})
export class AuthBackendHandler extends BackendAbstractService<unknown> {
  private cookieService: CookieService = inject(CookieService);

  activateAccount(confirmCode: string): Observable<null> {
    return of(null);
  }

  login(credentials: Credentials): Observable<LoginResponse> {
    let body = new HttpParams().set('email', credentials.username);
    if (credentials.password) {
      body = body.set('password', credentials.password);
    }
    return this.http.post<LoginResponse>(`${this.httpService.webApiUrl}Auth/Login`, body);
  }

  loginWithSso(authCode: string, websiteId: number): Observable<LoginResponse> {
    const body = new HttpParams().set('authCode', authCode).set('websiteId', websiteId);
    return this.http.post<LoginResponse>(`${this.httpService.webApiUrl}Auth/AuthCodeExchange`, body);
  }

  impersonate(): Observable<ImpersonationResponse> {
    const loginResponse: ImpersonationResponse = {
      email: this.cookieService.get(ImpersonateCookie.Email),
      userId: this.cookieService.get(ImpersonateCookie.UserId),
      tokens: {
        idToken: this.cookieService.get(ImpersonateCookie.IdToken),
        accessToken: this.cookieService.get(ImpersonateCookie.AccessToken),
        refreshToken: this.cookieService.get(ImpersonateCookie.RefreshToken),
        expiresIn: +this.cookieService.get(ImpersonateCookie.ExpiresIn),
      },
    };

    Object.keys(ImpersonateCookie).forEach((key) => this.cookieService.delete(ImpersonateCookie[key]));

    return of(loginResponse);
  }

  confirmCode(request: SmsChallengeRequest): Observable<LoginResponseInterface> {
    return this.http.post<LoginResponse>(`${this.httpService.webApiUrl}Auth/SmsMfaChallenge`, request);
  }

  requestPassword(email: string): Observable<null> {
    return this.http.post<null>(`${this.httpService.webApiUrl}Auth/ForgotPassword`, {
      email,
    });
  }

  resetPassword(newPassword: string, confirmationCode: string, email: string): Observable<null> {
    return this.http
      .post(`${this.httpService.webApiUrl}Auth/ConfirmForgotPassword`, { newPassword, confirmationCode, email })
      .pipe(map(() => null));
  }

  changePassword(currentPassword: string, newPassword: string, accessToken: string): Observable<void> {
    return this.http.post<void>(`${this.httpService.webApiUrl}Auth/ChangePassword`, {
      currentPassword,
      newPassword,
      accessToken,
    });
  }

  setupMfa(request: SetupMfaRequest): Observable<void> {
    return this.http.post<void>(`${this.httpService.webApiUrl}Auth/SetupMfa`, request);
  }

  signup(formData: unknown): Observable<null> {
    return of(null);
  }

  refreshToken(refreshToken: RefreshTokenRequest): Observable<LoginResponse> {
    return this.httpService.webApiUrl
      ? this.http.post<LoginResponse>(`${this.httpService.webApiUrl}Auth/RefreshToken`, refreshToken, {
          headers: new HttpHeaders({ [ignoreInterceptorHeaders.ignoreAuth]: 'true' }),
        })
      : of(null);
  }

  getSsoLogin(websiteId: number, websiteUrl: string): Observable<SsoLogin> {
    const safeWebsiteUrl = websiteUrl.replace(/^https?:\/\//, '');
    const params = new HttpParams().set('websiteId', websiteId).set('websiteUrl', encodeURIComponent(safeWebsiteUrl));

    return this.http.get<SsoLogin>(`${this.httpService.webApiUrl}Auth/SsoLogin`, { params });
  }

  validateInvitation(token: string): Observable<AcceptInvitationResponse> {
    const params: HttpParams = new HttpParams().set('token', token);
    return this.http.get<{
      isInvitationValid: boolean;
    }>(`${this.httpService.webApiUrl}Auth/ValidateInvitation`, { params });
  }

  acceptInvitation(acceptInvitation: AcceptInvitationRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.httpService.webApiUrl}Auth/AcceptInvitation`, acceptInvitation);
  }
}
